import { useTranslation } from 'react-i18next'

import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useExecuteOnce } from '@cash-web/shared-util-execute-once'
import { WebViewViewComponent } from '@squareup/customer-data-events-web'

import { ArcadeModalProps, ModalProps } from '../Modal/Modal'

interface UseModalSharedButtonProps {
  closeButtonProps?: Partial<ArcadeModalProps['closeButtonProps']>
  backButtonProps?: Partial<ArcadeModalProps['backButtonProps']>
}

/**
 * Hook for shared button props for Modal components.
 */
export const useModalSharedButtonProps = ({
  closeButtonProps,
  backButtonProps,
}: UseModalSharedButtonProps): Pick<ArcadeModalProps, 'closeButtonProps' | 'backButtonProps'> => {
  const { t } = useTranslation()

  return {
    closeButtonProps: closeButtonProps?.onClose
      ? {
          ...closeButtonProps,
          onClose: closeButtonProps.onClose,
          ariaLabel: closeButtonProps?.ariaLabel ?? t('Close modal', { context: 'aria label on a button' }),
        }
      : undefined,
    backButtonProps: backButtonProps?.onBack
      ? {
          ...backButtonProps,
          onBack: backButtonProps.onBack,
          ariaLabel:
            backButtonProps?.ariaLabel ?? t('Go back', { context: 'aria label to go back to the last screen' }),
        }
      : undefined,
  }
}

/**
 * Hook for shared props for Modal components.
 */
export const useModalSharedProps = ({ closeButtonProps, backButtonProps, ...props }: ModalProps): ArcadeModalProps => {
  const { t } = useTranslation()
  const logCdfEvent = useLogCashWebCdfEvent()
  const modalSharedButtonProps = useModalSharedButtonProps({ closeButtonProps, backButtonProps })

  useExecuteOnce(() => {
    logCdfEvent(WebViewViewComponent({ componentName: 'Modal', componentType: 'Open' }))
  })

  return {
    ariaLabel: t('Modal', { context: 'aria label for modal dialog' }),
    ...modalSharedButtonProps,
    ...props,
  }
}
